import React, { useMemo, useContext, memo, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query'; 
import AuthContext from 'flexbiz-core/components/auth/Context';

import ReportFrame from './ReportFrame'; 
import DataPivotViewer from './DataPivotViewer'; 
import { nguCanhCauHinh } from './dashboardCongTy';

// ============================================================================
// COMPONENT MẸ: DASHBOARD WIDGET
// ============================================================================
const DashboardWidget = memo(({ pivotConfigId, refreshTrigger, onMetaLoaded, frameRef }) => {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');
  
  const reportRef = useRef(null);
  // Chuyển ref của ReportFrame ra ngoài (widget dashboard đọc điều kiện đang hiện để mở trang báo cáo gốc).
  const ganRef = useCallback((r) => {
    reportRef.current = r;
    if (typeof frameRef === 'function') frameRef(r);
    else if (frameRef) frameRef.current = r;
  }, [frameRef]);

  const autoRefreshInterval = useMemo(() => {
    const minMs = 5 * 60 * 1000;
    const maxMs = 10 * 60 * 1000;
    return Math.floor(Math.random() * (maxMs - minMs + 1) + minMs);
  }, []);

  // pivotconfig là bản ghi của CÔNG TY SỞ HỮU DASHBOARD: widget chạy với dữ liệu công ty khác
  // (widgetConfig.id_apps — xem dashboardCongTy.js) vẫn phải đọc cấu hình ở công ty gốc, còn
  // số liệu (ReportFrame bên dưới) thì chạy theo công ty của widget.
  const ctxCauHinh = nguCanhCauHinh(context);
  const { data: configMeta, isLoading, error } = useQuery({
    queryKey: ['pivotconfig_meta', ctxCauHinh.appInfo?._id, pivotConfigId],
    queryFn: async () => {
      const confRes = await ctxCauHinh.apis.asyncGetList(userToken, 'pivotconfig', { condition: { _id: pivotConfigId }, limit: 1 });
      const confItem = (confRes?.data || confRes || [])[0];
      
      if (!confItem) throw new Error("Không tìm thấy cấu hình");
      
      const parsedConfig = JSON.parse(confItem.config);
      return { 
        title: confItem.title, 
        code: confItem.code, 
        apiCode: parsedConfig.apiCode || confItem.code, 
        ...parsedConfig 
      };
    },
    enabled: !!pivotConfigId && !!ctxCauHinh.appInfo?._id,
    staleTime: Infinity, 
  });

  useEffect(() => {
    if (configMeta && onMetaLoaded) {
        onMetaLoaded(configMeta);
    }
  }, [configMeta, onMetaLoaded]);

  useEffect(() => {
    if (refreshTrigger) {
      reportRef.current?.refresh();
    }
  }, [refreshTrigger]);

  if (isLoading) return <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2, color: 'error.main' }}>Lỗi tải Widget: {error.message}</Box>;
  if (!configMeta) return null;

  const reportConfig = context?.config?.getReportConfig ? context.config.getReportConfig(configMeta.code) : {};
  
  // 👇 ĐÃ SỬA: Ưu tiên lấy columns từ cấu hình đã lưu (configMeta), nếu không có mới lấy từ core
  const columns = configMeta.columns || reportConfig?.columns || reportConfig?.displayFields || [];

  return (
    <ReportFrame 
      ref={ganRef}
      isWidget={true}
      code={configMeta.code}
      apiCode={configMeta.apiCode}
      defaultCondition={configMeta.reportCondition || {}}
      filterFields={configMeta.filterFields || reportConfig?.filterFields}
      enableLoadMore={false} 
      refreshInterval={autoRefreshInterval}
      contentSx={{ p: 0 }} 
      containerSx={{ height: '100%' }}
      handleDataScript={configMeta.handleDataScript}
      renderReport={(reportData) => (
         <DataPivotViewer 
            data={reportData} 
            columns={columns} 
            isWidget={true} 
            configMeta={configMeta} 
         />
      )}
    />
  );
});

export default DashboardWidget;